<template>
  <div class="app-container" style="padding: 0 2rem;">
    <h1>Compliance Tracker</h1>
    <h2>Do you have a provider in mind? Search them here.</h2>
    <h4>You will receive a baseline report within 24 hours. Additional reports to be provided as compliance status changes.</h4>
    <div class="compliance-disclaimer" role="note" aria-label="Selection disclaimer">
      <strong>Please confirm you selected the correct provider option.</strong>
      <span>
        Review the organization name and details carefully before signing up for compliance alerts.
      </span>
    </div>

    <div id="results-section">
      <div class="col-6 d-flex flex-col left-col">
        <div class="filter-section">
          <div id="addressInput">
            <input
              v-model="query"
              type="text"
              placeholder="Search by Company Name"
              ref="addressInput"
              @keyup.enter.prevent="loadItemsDebounced"
            >     
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M14 14L11.1 11.1M12.6667 7.33333C12.6667 10.2789 10.2789 12.6667 7.33333 12.6667C4.38781 12.6667 2 10.2789 2 7.33333C2 4.38781 4.38781 2 7.33333 2C10.2789 2 12.6667 4.38781 12.6667 7.33333Z" stroke="#98A2B3" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </div>

          <button :disabled="!query" class="isl-btn isl-btn-primary" @click="loadItemsDebounced">
            Search
          </button>
        </div>

        <template v-if="searched">
          <h3>
            Select Results You're Interested In
          </h3>
          <div v-if="items.length" ref='listSection'>
            <ListItem
              v-for="(item, i) in items"
              :key="item.id"
              :value="item"
              @select="select(i)"
            />
          </div>
          <NoResults v-else />
        </template>
      </div>

      <div class="col-6">
        <div id="right-col">
          <DirectoryHubBanner v-if="!selected.length" />
          <ComplianceAlertsForm v-else :items="selected" @close="clearSelection"/>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from "axios";
import NoResults from "@/components/NoResults.vue";
import ListItem from "./ListItem.vue";
import DirectoryHubBanner from "./DirectoryHubBanner.vue";
import ComplianceAlertsForm from "./ComplianceAlertsForm.vue";

export default {
  components: {
    ListItem,
    NoResults,
    DirectoryHubBanner,
    ComplianceAlertsForm,
  },
  data() {
    return {
      loading: false,
      searched: false,
      query: '',
      items: [],
      debounce: null
    };
  },
  watch: {
    center: 'doTheSearch',
    radius: 'doTheSearch',
    filters: {
      deep: true,
      handler: 'applyFilters'
    },
    filterOptionsValue: {
      deep: true,
      handler (v, o) {
        if (o.length) {
          this.applyFilters()
        }
      }
    },
  },
  computed: {
    selected () {
      const value = []

      for (let i = 0; i < this.items.length; i++) {
        const element =  this.items[i]
        if (element.selected) {
          value.push(element)
        }
      }

      return value
    }
  },
  methods: {
    loadItemsDebounced() {
      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => {
        this.loadItems();
      }, 300);
    },
    async loadItems () {
      const options = {
        method: 'POST',
        url: '/wp-admin/admin-ajax.php',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        data: {
          action: 'org_search_organizations',
          query: this.query,
        }
      };

      try {
        this.loading = true
        const { data } = await axios.request(options)
        this.items = data
      } catch (err) {
        alert(err)
      } finally {
        this.loading = false
        this.searched = true
      }
    },
    select (i) {
      this.items[i].selected = !this.items[i].selected
    },
    clearSelection () {
      for (let i = 0; i < this.items.length; i++) {
        this.items[i].selected = false
      }
    }
  }
};
</script>

<style>

#alerts-app * {
    font-weight: 400;
    font-size: 14px;
    box-sizing: border-box;
}

#alerts-app h1 {
  /* Find Services Near You */

  margin: 50px 0 20px 0;

  font-family: 'Arial';
  font-style: normal;
  font-weight: 700;
  font-size: 42px;
  line-height: 48px;

  color: #000000;
}

#alerts-app h2 {
  color: #000;
  font-family: Arial;
  font-size: 24px;
  font-weight: 700;
  padding: 0;
  margin: 0;
}

#alerts-app h3 {
  margin-top: 3rem;
  margin-bottom: 1.5rem;
  font-family: 'Arial';
  font-style: normal;
  font-weight: 400;
  font-size: 26px;
  line-height: 30px;
  color: #000000;
}

#alerts-app h4 {
  font-size: 20px;
  font-weight: 400;
  margin-top: 11px;
}

#alerts-app .compliance-disclaimer {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 24px;
  padding: 18px 20px;
  border: 1px solid #b8d8fb;
  border-left: 6px solid #00182b;
  background: #eef7ff;
  color: #00182b;
  border-radius: 18px;
}

#alerts-app .compliance-disclaimer strong {
  font-size: 16px;
  font-weight: 700;
}

#alerts-app .compliance-disclaimer span {
  font-size: 14px;
  line-height: 1.5;
}

#addressInput {
  position: relative;
  flex: 1 0 auto;
  margin-right: 1rem;
}

#addressInput input {
  padding-right: 44px;
}

#addressInput svg {
  position: absolute;
  content: '';
  width: 16px;
  height: 16px;
  top: 50%;
  right: 14px;
  transform: translateY(-50%);
}

#results-section {
  box-sizing: border-box;
  margin-top: 60px;
}

.filter-section {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
}

@media screen and (min-width: 768px) {
  #alerts-app .col-6 {
    width: 50%;
  }

  #alerts-app .left-col {
    padding-right: 40px;
  }

  #right-col {
    position: sticky;
    top: 20px;
  }
}


@media screen and (max-width: 767px) {
  #alerts-app .col-6 {
    width: 100%;
  }
}
</style>
